import React, { useState, useEffect, useRef, useCallback } from "react";

const AddressForm = ({ ontvanggegevs, input, label, meta: { asyncValidating, touched, error } }) => {
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [address, setAddress] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const init = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        const dummyElement = document.createElement("div");
        placesService.current = new window.google.maps.places.PlacesService(dummyElement);
        setIsGoogleMapsLoaded(true);
        return true;
      }
      return false;
    };

    if (!init()) {
      const interval = setInterval(() => {
        if (cancelled) return;
        if (init()) clearInterval(interval);
      }, 200);
      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = useCallback((e) => {
    const newAddress = e.target.value;
    setAddress(newAddress);

    if (!autocompleteService.current || newAddress.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    autocompleteService.current.getPlacePredictions(
      {
        input: newAddress,
        componentRestrictions: { country: "nl" },
      },
      (predictions, status) => {
        setLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    );
  }, []);

  const getAddressParts = (place) => {
    let addressObj = {};
    const address_components = place.address_components || [];
    address_components.forEach((element) => {
      addressObj[element.types[0]] = element.short_name;
    });
    return addressObj;
  };

  const handleSelect = useCallback((suggestion) => {
    if (!placesService.current) return;

    setAddress(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);

    placesService.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: ["address_components", "geometry", "formatted_address"],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const adressParts = getAddressParts(place);
          const latLng = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          const imputname = input.name;
          ontvanggegevs(suggestion.description, adressParts, latLng, imputname);
        }
      }
    );
  }, [input.name, ontvanggegevs]);

  // Fallback input if Google Maps is not loaded
  if (!isGoogleMapsLoaded) {
    return (
      <div>
        <label>{label}</label>
        <div className={asyncValidating ? "async-validating" : ""}>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Voer adres handmatig in (Google Maps niet beschikbaar)"
            className="form-control"
          />
          <small className="text-muted">
            Google Maps API is niet geladen. Voer het adres handmatig in.
          </small>
          {touched && error && typeof error === 'string' && <span className="text-danger">{error}</span>}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <label>{label}</label>
      <div className={asyncValidating ? "async-validating" : ""} style={{ position: "relative" }}>
        <input
          ref={inputRef}
          type="text"
          value={address}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder="Zoek het adres"
          className="location-search-input form-control"
        />
        {showSuggestions && (
          <div
            className="autocomplete-dropdown-container"
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              borderTop: "none",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {loading && <div style={{ padding: "8px 12px" }}>Loading...</div>}
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.place_id}
                onClick={() => handleSelect(suggestion)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  backgroundColor: "#ffffff",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#fafafa")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "#ffffff")}
              >
                <span>{suggestion.description}</span>
              </div>
            ))}
          </div>
        )}
        {touched && error && typeof error === 'string' && <span className="text-danger">{error}</span>}
      </div>
    </div>
  );
};

export default AddressForm;
